import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Bot,
  CheckCircle2,
  ClipboardCheck,
  ExternalLink,
  Inbox,
  Loader2,
  RefreshCw,
  ShieldAlert,
  ShoppingBag,
} from "lucide-react";
import { AdminGate } from "@/components/admin/AdminGate";
import { AdminLayout } from "@/components/admin/AdminLayout";
import {
  AdminEmptyState,
  AdminLoading,
  AdminMetric,
  AdminPage,
  AdminPanel,
  AdminStatusPill,
  adminToneForStatus,
} from "@/components/admin/AdminPrimitives";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import {
  amazonTakedownCapability,
  amazonTakedownCapabilityLabel,
} from "@/lib/amazonAccess";

type OpsSummary = {
  metrics: {
    flaggedQueueCount: number;
    pendingAuditCount: number;
    manualQaCount: number;
    pendingManualCount: number;
    agentNeedsHumanCount: number;
    failedScrapeCount: number;
    brandBlockerCount: number;
    customerAccessBlockerCount: number;
    storefrontFailedCount: number;
  };
  retail: {
    needsLogin: boolean;
    activeContextCount: number;
    pendingManualFallbackCount: number;
    oldestPendingManualFallback: {
      id: string;
      asin: string;
      createdAt: string;
    } | null;
  };
  flaggedReviews: Array<{
    id: string;
    asin: string;
    rating: number;
    title: string | null;
    body: string;
    classification: {
      actionStatus: string;
      violations: unknown;
      takedownRequest: string | null;
    } | null;
    auditRequest: { id: string; email: string; createdAt: string };
  }>;
  manualQaScrapes: Array<{
    id: string;
    asin: string;
    submittedAt: string | null;
    claimedAt: string | null;
    manualScraper: { id: string; email: string; name: string | null } | null;
    monitoredAsin: {
      productTitle: string | null;
      brand: { name: string };
    } | null;
    auditRequest: { id: string; email: string } | null;
  }>;
  agentHandoffs: Array<{
    id: string;
    humanReason: string | null;
    humanRequestedAt: string | null;
    humanExpiresAt: string | null;
    lastCheckpoint: string | null;
    startedAt: string;
    reviewClassification: {
      review: { asin: string; auditRequest: { email: string } };
    };
  }>;
  customerBlockers: Array<{
    id: string;
    name: string;
    brandRegistry: boolean;
    brandRegistryAnsweredAt: string | null;
    collaboratorStatus: string;
    storefrontDiscoveryStatus: string;
    storefrontDiscoveryAt: string | null;
    asinCount: number;
    customer: { id: string; email: string; organizationName: string };
  }>;
  failedScrapes: Array<{
    id: string;
    asin: string;
    source: "auto" | "human";
    errorCategory: string | null;
    errorMessage: string | null;
    createdAt: string;
    monitoredAsin: {
      productTitle: string | null;
      brand: { name: string };
    } | null;
    auditRequest: { id: string; email: string } | null;
  }>;
  recentAudits: Array<{
    id: string;
    email: string;
    status: string;
    inputAsins: string[];
    scrapedReviewCount: number;
    flaggedCount: number;
    createdAt: string;
  }>;
};

function formatDate(value: string | null | undefined) {
  if (!value) return "No date";
  return new Date(value).toLocaleDateString();
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return "No timestamp";
  return new Date(value).toLocaleString();
}

function snippet(value: string | null | undefined, max = 120) {
  const normalized = (value ?? "").replace(/\s+/g, " ").trim();
  if (!normalized) return "No text";
  return normalized.length > max ? `${normalized.slice(0, max).trim()}...` : normalized;
}

function violationLabels(value: unknown) {
  if (!Array.isArray(value)) return "No violations";
  const labels = value
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const record = item as Record<string, unknown>;
      return typeof record.type === "string" ? record.type.replaceAll("_", " ") : null;
    })
    .filter(Boolean);
  return labels.length > 0 ? labels.join(", ") : "No violations";
}

function RowLink({
  to,
  icon,
  title,
  meta,
  children,
}: {
  to: string;
  icon: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <Link
      to={to}
      className="group grid gap-2 border-b border-border px-4 py-3 last:border-b-0 hover:bg-muted/40 sm:grid-cols-[1fr_auto]"
    >
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-muted-foreground">{icon}</span>
          <div className="min-w-0 truncate text-sm font-medium">{title}</div>
        </div>
        {children && (
          <div className="mt-1 line-clamp-2 text-sm leading-5 text-muted-foreground">
            {children}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {meta}
        <ArrowRight className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
    </Link>
  );
}

function AdminHomeInner() {
  const [summary, setSummary] = useState<OpsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api<OpsSummary>("/admin/ops-summary").then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (!res.ok || !res.data) {
        setError(res.error ?? "Could not load admin summary.");
        setSummary(null);
        return;
      }
      setSummary(res.data);
    });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  return (
    <AdminPage
      eyebrow="Operations"
      title="Today"
      description="Start with blockers, then work the queues that move reviews from flagged to submitted or removed."
      actions={
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setReloadKey((key) => key + 1)}
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Refresh
        </Button>
      }
    >
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </div>
      )}

      {loading && !summary && <AdminLoading label="Loading admin summary" />}

      {summary && (
        <>
          {summary.retail.needsLogin && (
            <AdminPanel className="border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              <div className="flex flex-wrap items-center gap-3 px-4 py-4">
                <ShoppingBag className="h-5 w-5" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium">Amazon retail auth is down</div>
                  <div className="text-sm opacity-80">
                    Authenticated scraping cannot run until a retail context is active.
                    {summary.retail.oldestPendingManualFallback && (
                      <>
                        {" "}
                        Oldest auto-routed ASIN is{" "}
                        <span className="font-mono">
                          {summary.retail.oldestPendingManualFallback.asin}
                        </span>
                        .
                      </>
                    )}
                  </div>
                </div>
                <Link to="/admin/retail-accounts">
                  <Button size="sm" className="bg-amber-900 text-white hover:bg-amber-800">
                    Re-login
                  </Button>
                </Link>
              </div>
            </AdminPanel>
          )}

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <AdminMetric
              label="Takedown inbox"
              value={summary.metrics.flaggedQueueCount}
              detail="Flagged reviews awaiting action"
              tone={summary.metrics.flaggedQueueCount > 0 ? "warning" : "success"}
              to="/admin/queue"
            />
            <AdminMetric
              label="Manual QA"
              value={summary.metrics.manualQaCount}
              detail={`${summary.metrics.pendingManualCount} unclaimed scrape assignments`}
              tone={summary.metrics.manualQaCount > 0 ? "warning" : "neutral"}
              to="/admin/scrapes?status=submitted"
            />
            <AdminMetric
              label="Agent handoffs"
              value={summary.metrics.agentNeedsHumanCount}
              detail="Automation waiting for an operator"
              tone={summary.metrics.agentNeedsHumanCount > 0 ? "danger" : "success"}
              to="/admin/agent-runs?status=needs_human"
            />
            <AdminMetric
              label="Brand blockers"
              value={summary.metrics.brandBlockerCount}
              detail={`${summary.metrics.customerAccessBlockerCount} missing or pending Amazon access · ${summary.metrics.storefrontFailedCount} failed storefront syncs`}
              tone={summary.metrics.brandBlockerCount > 0 ? "warning" : "success"}
              to="/admin/customers"
            />
          </div>

          <div className="grid gap-6 xl:grid-cols-2">
            <AdminPanel
              title="Work next"
              description="The shortest path to billable outcomes."
              actions={
                <Link to="/admin/queue" className="text-sm font-medium text-foreground hover:underline">
                  Open inbox
                </Link>
              }
            >
              {summary.flaggedReviews.length === 0 ? (
                <AdminEmptyState
                  title="No takedowns waiting"
                  description="Flagged reviews will appear here after classification."
                />
              ) : (
                summary.flaggedReviews.map((review) => (
                  <RowLink
                    key={review.id}
                    to={`/admin/audits/${review.auditRequest.id}`}
                    icon={<Inbox className="h-4 w-4" />}
                    title={
                      <>
                        {review.auditRequest.email} ·{" "}
                        <span className="font-mono">{review.asin}</span>
                      </>
                    }
                    meta={<span>{formatDate(review.auditRequest.createdAt)}</span>}
                  >
                    {review.rating}/5 · {violationLabels(review.classification?.violations)}
                    {" · "}
                    {snippet(review.title || review.body)}
                  </RowLink>
                ))
              )}
            </AdminPanel>

            <AdminPanel
              title="Manual scrape QA"
              description="Worker submissions that need approval before reviews are written."
              actions={
                <Link
                  to="/admin/scrapes?status=submitted"
                  className="text-sm font-medium text-foreground hover:underline"
                >
                  Open QA
                </Link>
              }
            >
              {summary.manualQaScrapes.length === 0 ? (
                <AdminEmptyState
                  title="No submissions to QA"
                  description="Submitted scrape assignments will land here."
                />
              ) : (
                summary.manualQaScrapes.map((scrape) => (
                  <RowLink
                    key={scrape.id}
                    to={`/admin/scrapes/${scrape.id}`}
                    icon={<ClipboardCheck className="h-4 w-4" />}
                    title={
                      <>
                        <span className="font-mono">{scrape.asin}</span>
                        {scrape.monitoredAsin?.brand.name
                          ? ` · ${scrape.monitoredAsin.brand.name}`
                          : ""}
                      </>
                    }
                    meta={<span>{formatDateTime(scrape.submittedAt)}</span>}
                  >
                    Submitted by{" "}
                    {scrape.manualScraper?.name || scrape.manualScraper?.email || "unknown worker"}
                    {scrape.auditRequest?.email ? ` for ${scrape.auditRequest.email}` : ""}
                  </RowLink>
                ))
              )}
            </AdminPanel>

            <AdminPanel
              title="Agent handoffs"
              description="Automation runs waiting for live intervention or manual resolution."
              actions={
                <Link
                  to="/admin/agent-runs?status=needs_human"
                  className="text-sm font-medium text-foreground hover:underline"
                >
                  Open handoffs
                </Link>
              }
            >
              {summary.agentHandoffs.length === 0 ? (
                <AdminEmptyState
                  title="No agent handoffs"
                  description="Runs that need a human will appear here with the live browser action."
                />
              ) : (
                summary.agentHandoffs.map((run) => (
                  <RowLink
                    key={run.id}
                    to={`/admin/agent-runs/${run.id}`}
                    icon={<Bot className="h-4 w-4" />}
                    title={
                      <>
                        {run.reviewClassification.review.auditRequest.email} ·{" "}
                        <span className="font-mono">
                          {run.reviewClassification.review.asin}
                        </span>
                      </>
                    }
                    meta={
                      <AdminStatusPill tone="danger">
                        {run.humanExpiresAt
                          ? `expires ${formatDateTime(run.humanExpiresAt)}`
                          : "needs human"}
                      </AdminStatusPill>
                    }
                  >
                    {run.humanReason || run.lastCheckpoint || "Agent requested operator help."}
                  </RowLink>
                ))
              )}
            </AdminPanel>

            <AdminPanel
              title="Account blockers"
              description="Customers and brands that cannot move cleanly through monitoring."
              actions={
                <Link to="/admin/customers" className="text-sm font-medium text-foreground hover:underline">
                  Customers
                </Link>
              }
            >
              {summary.customerBlockers.length === 0 ? (
                <AdminEmptyState
                  title="No account blockers"
                  description="Amazon access and storefront sync look clear."
                />
              ) : (
                summary.customerBlockers.map((brand) => (
                  <RowLink
                    key={brand.id}
                    to={`/admin/customers/${brand.customer.id}`}
                    icon={<ShieldAlert className="h-4 w-4" />}
                    title={`${brand.customer.organizationName} · ${brand.name}`}
                    meta={<span>{brand.asinCount} ASINs</span>}
                  >
                    <span className="inline-flex flex-wrap gap-2">
                      <AdminStatusPill
                        tone={adminToneForStatus(
                          amazonTakedownCapability({
                            brandRegistry: brand.brandRegistry,
                            brandRegistryAnsweredAt:
                              brand.brandRegistryAnsweredAt,
                            collaboratorStatus: brand.collaboratorStatus as
                              | "none"
                              | "invite_sent"
                              | "verified",
                          })
                        )}
                      >
                        Takedowns:{" "}
                        {amazonTakedownCapabilityLabel(
                          amazonTakedownCapability({
                            brandRegistry: brand.brandRegistry,
                            brandRegistryAnsweredAt:
                              brand.brandRegistryAnsweredAt,
                            collaboratorStatus: brand.collaboratorStatus as
                              | "none"
                              | "invite_sent"
                              | "verified",
                          })
                        )}
                      </AdminStatusPill>
                      <AdminStatusPill tone={adminToneForStatus(brand.storefrontDiscoveryStatus)}>
                        Storefront: {brand.storefrontDiscoveryStatus}
                      </AdminStatusPill>
                    </span>
                  </RowLink>
                ))
              )}
            </AdminPanel>
          </div>

          <div className="grid gap-6 xl:grid-cols-2">
            <AdminPanel
              title="Failed scrapes"
              description="Failures worth triaging before retries pile up."
              actions={
                <Link to="/admin/scrapes?status=failed" className="text-sm font-medium text-foreground hover:underline">
                  View failures
                </Link>
              }
            >
              {summary.failedScrapes.length === 0 ? (
                <AdminEmptyState
                  title="No failed scrapes"
                  description="Recent scrape attempts are not reporting failures."
                />
              ) : (
                summary.failedScrapes.map((scrape) => (
                  <RowLink
                    key={scrape.id}
                    to={`/admin/scrapes/${scrape.id}`}
                    icon={<AlertTriangle className="h-4 w-4" />}
                    title={
                      <>
                        <span className="font-mono">{scrape.asin}</span> · {scrape.source}
                      </>
                    }
                    meta={<span>{formatDateTime(scrape.createdAt)}</span>}
                  >
                    {scrape.errorCategory || "Failure"} · {snippet(scrape.errorMessage, 140)}
                  </RowLink>
                ))
              )}
            </AdminPanel>

            <AdminPanel
              title="Recent audits"
              description="Newest lead and monitoring runs."
              actions={
                <Link to="/admin/audits" className="text-sm font-medium text-foreground hover:underline">
                  All audits
                </Link>
              }
            >
              {summary.recentAudits.length === 0 ? (
                <AdminEmptyState
                  title="No audits yet"
                  description="New audits and monitoring runs will appear here."
                />
              ) : (
                summary.recentAudits.map((audit) => (
                  <RowLink
                    key={audit.id}
                    to={`/admin/audits/${audit.id}`}
                    icon={
                      audit.status === "completed" ? (
                        <CheckCircle2 className="h-4 w-4" />
                      ) : (
                        <ExternalLink className="h-4 w-4" />
                      )
                    }
                    title={audit.email}
                    meta={
                      <AdminStatusPill tone={adminToneForStatus(audit.status)}>
                        {audit.status}
                      </AdminStatusPill>
                    }
                  >
                    {audit.inputAsins.length} ASINs · {audit.scrapedReviewCount} reviews ·{" "}
                    {audit.flaggedCount} flagged · {formatDate(audit.createdAt)}
                  </RowLink>
                ))
              )}
            </AdminPanel>
          </div>
        </>
      )}
    </AdminPage>
  );
}

export default function AdminHome() {
  return (
    <AdminGate>
      <AdminLayout>
        <AdminHomeInner />
      </AdminLayout>
    </AdminGate>
  );
}
